// ChatList.js — Responsive: iOS, Android, Desktop, Tablet

import React from "react";
import BottomTabs from "./BottomTabs";
import { TABS } from "../constants/tabs";
import { usePwaInstall } from "../hooks/usePwaInstall";

const formatTime = (t) => {
  if (!t) return "";
  const d = new Date(t);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};


const IconSearch = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
       <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

/* Network / signal bars — opens Buy Data */
const IconNetwork = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 20v-4" />
    <path d="M9 20v-8" />
    <path d="M14 20V8" />
    <path d="M19 20V4" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────────────────────
   ⏰ Live countdown for a scheduled message preview.
─────────────────────────────────────────────────────────────────────────────── */
function ScheduledCountdown({ scheduledAt }) {
  const [label, setLabel] = React.useState("");

  React.useEffect(() => {
    const compute = () => {
      const ms = new Date(scheduledAt) - Date.now();
      if (ms <= 0) { setLabel("Sending…"); return; }
      const totalSec = Math.floor(ms / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (h > 0)      setLabel(`⏰ ${h}h ${m}m`);
      else if (m > 0) setLabel(`⏰ ${m}m ${s}s`);
      else            setLabel(`⏰ ${s}s`);
    };

    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [scheduledAt]);

  return (
    <span className="text-amber-500 dark:text-amber-400 font-medium">
      {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   🪦 Resolve last-message preview text for the sidebar row.
   Handles plain strings, message objects, tombstoned messages, and scheduled.
─────────────────────────────────────────────────────────────────────────────── */
function resolveLastMessagePreview(msg) {
  if (!msg) return null;

  // Plain string (legacy / simple case)
  if (typeof msg === "string") {
    return { text: msg, isDeleted: false, isScheduled: false, scheduledAt: null };
  }

  // Tombstoned message object
  if (typeof msg === "object") {
    // 🫥 hidden (delete for me)
    if (msg.hiddenForMe) {
      return null; // completely ignore
    }

    // 🪦 deleted for everyone
    if (msg.isDeleted) {
      return {
        text:        msg.text || "This message was deleted",
        isDeleted:   true,
        isScheduled: false,
        scheduledAt: null,
      };
    }
  }

  // Scheduled message object — written by updateLocalPreview in useScheduledMessage
  if (typeof msg === "object" && msg.isScheduled && msg.scheduledAt) {
    return {
      text:        msg.text || "📎 Media",
      isDeleted:   false,
      isScheduled: true,
      scheduledAt: msg.scheduledAt,
    };
  }

  // Normal message object
  const text =
    msg.text ||
    (msg.mediaType || msg.media ? "📎 Media" : "");

  return { text, isDeleted: false, isScheduled: false, scheduledAt: null };
}

/* ─────────────────────────────────────────────────────────────────────────────
   Helper: resolve the best display label for a user item.
   Prefers displayName, falls back to username.
─────────────────────────────────────────────────────────────────────────────── */
const resolveDisplayName = (item) =>
  item.displayName || item.username || "";

export default function ChatList({
  users,
  groups,
  cachedChatList = [],
  typingUsers = [],
  groupTyping = {},
    hasUnreadStatus = false,
   statusSocket, 

  // ✅ P2P
  p2pOrders = [],
  orderTimers = [],
  myChatId,

  searchQuery,
  setSearchQuery,
  selectedItem,
  setSelectedItem,
  menuOpen,
  setMenuOpen,
  mobileView,
  createGroupMode,
  setCreateGroupMode,
  menuRef,
  activeTab,
  setActiveTab,
  onOpenConnect,
  onOpenContacts,
  onLogout,
  isLoggedIn,
  onOpenAvatar,
  setDefaultTab,
}) {
  
 
  const { canInstall, isIOSInstallable, handleInstall } = usePwaInstall();
  const [showIOSInstallHelp, setShowIOSInstallHelp] = React.useState(false);
  const storedDefault = localStorage.getItem("defaultTab") || TABS.FEED;

  const resolveUserName = (chatId, members = []) => {
    const found = members.find((m) => String(m.chatId) === String(chatId));
    // prefer displayName on group members too
    return found?.displayName || found?.userName || found?.username || "Someone";
  };

  const getTypingText = (item) => {
    if (item.type === "user") {
      if (typingUsers.includes(item.chatId)) return "typing…";
    }
    if (item.type === "group") {
      const typers = groupTyping[item.groupId];
      if (Array.isArray(typers) && typers.length > 0) {
        const names = typers.slice(0, 2).map((id) => resolveUserName(id, item.members));
        if (typers.length === 1) return `${names[0]} typing…`;
        if (typers.length === 2) return `${names[0]}, ${names[1]} typing…`;
        return `${names[0]} and ${typers.length - 1} others typing…`;
      }
    }
    return null;
  };

  const getP2PPreview = (item) => {
    if (item.type !== "user") {
      return null;
    }

    const order = p2pOrders.find(
      (o) =>
        (
          String(o.sellerChatId) === String(item.chatId) ||
          String(o.buyerChatId)  === String(item.chatId)
        ) &&
        ["pending", "paid"].includes(o.status)
    );

    if (!order) {
      return null;
    }

    const remaining = orderTimers?.[order.orderId];

    if (remaining == null || remaining <= 0) {
      return `Order ${order.orderId} has expired`;
    }

    const total = (
      Number(order.amount || 0) * Number(order.rate || 0)
    ).toLocaleString();

    return String(order.buyerChatId) === String(myChatId)
      ? `Pay ${total} ${order.currency || ""} for ${order.amount} ${order.asset || ""} within ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`
      : `Buyer pays ${total} ${order.currency || ""} for ${order.amount} ${order.asset || ""} in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
  };

  // ── Build live list from socket data ──────────────────────────────
  const liveList = [
    ...users.map((u) => ({
      type:            "user",
      chatId:          u.chatId,
      username:        u.username,
      displayName:     u.displayName || null,   // ← carry displayName through
      profileImageUrl: u.profileImageUrl || null,
      online:          u.online ?? false,
      unreadCount:     u.unreadCount || 0,
      lastActivityAt:  u.lastActivityAt || null,
      lastMessage:     u.lastMessage || null,
    })),
    ...groups.map((g) => ({
      type:            "group",
      groupId:         g.groupId,
      name:            g.name,
      members:         Array.isArray(g.members) ? g.members : [],
      unreadCount:     g.unreadCount || 0,
      lastActivityAt:  g.lastActivityAt || null,
      lastMessage:     g.lastMessage || null,
      groupImageUrl:   g.groupImageUrl || null,
    })),
  ];

  // ── Build cache list from IndexedDB cold-start data ───────────────
  const cacheList = cachedChatList.map((entry) => {
    if (entry.type === "user") {
      return {
        type:            "user",
        chatId:          entry.peerIdOrGroupId,
        username:        entry.displayName,        // cache stores displayName in this field
        displayName:     entry.displayName || null,
        profileImageUrl: entry.profileImageUrl || null,
        online:          false,
        unreadCount:     entry.unreadCount || 0,
        lastActivityAt:  entry.lastActivityAt || null,
        lastMessage:     entry.lastMessage || null,
        _fromCache:      true,
      };
    }
    return {
      type:            "group",
      groupId:         entry.peerIdOrGroupId,
      name:            entry.displayName,
      members:         [],
      unreadCount:     entry.unreadCount || 0,
      lastActivityAt:  entry.lastActivityAt || null,
      lastMessage:     entry.lastMessage || null,
      groupImageUrl:   entry.profileImageUrl || null,
      _fromCache:      true,
    };
  });

  // ── Merge: prefer live data, fall back to cache ───────────────────
  const liveKeys = new Set([
    ...users.map((u)  => `user-${u.chatId}`),
    ...groups.map((g) => `group-${g.groupId}`),
  ]);

  const combinedList = [
    ...liveList,
    ...cacheList.filter((c) => {
      const k = c.type === "user" ? `user-${c.chatId}` : `group-${c.groupId}`;
      return !liveKeys.has(k);
    }),
  ];

  // ── Count CHATS with unread messages, not total message count ──
   const unreadChatCount = combinedList.filter((c) => {
    if ((c.unreadCount || 0) <= 0) return false;
    const isOpen =
      (c.type === "user"  && selectedItem?.type === "user"  && selectedItem.chatId  === c.chatId) ||
      (c.type === "group" && selectedItem?.type === "group" && selectedItem.groupId === c.groupId);
    return !isOpen;
  }).length;

  const filtered = combinedList
    .filter((item) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return item.type === "user"
        // search across both displayName and username so either works
        ? (
            (item.displayName || "").toLowerCase().includes(q) ||
            (item.username    || "").toLowerCase().includes(q)
          )
        : (item.name || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const ta = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
      const tb = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
      return tb - ta;
    });

  return (
    <aside
      className={`
        relative z-10
        flex h-full w-full flex-col border-r
        bg-white dark:bg-neutral-900
        border-neutral-200 dark:border-neutral-800
        ${mobileView ? (selectedItem ? "hidden" : "w-full") : "w-full max-w-xs lg:max-w-sm"}
      `}
    >
   {/* APP TITLE */}
<div
  className="px-4 pt-4"
  style={{ paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}
>
   <div className="flex items-center justify-between">
    <div className="text-xl sm:text-2xl font-bold text-green-600">
      Metisel
    </div>

    <div className="flex items-center gap-1">
      {/* SEARCH → CONNECT PAGE */}
      <button
        onClick={onOpenConnect}
        className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-900 dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 active:bg-neutral-300 dark:active:bg-neutral-600 touch-manipulation"
               aria-label="Search"
        title="Search"
      >
       
        <IconSearch />
      </button>

      {/* NETWORK → BUY DATA PAGE */}
      <button
        onClick={() => {
          setMenuOpen(false);
          setSelectedItem(null);
          setActiveTab(TABS.BUY_DATA);
        }}
        className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-900 dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 active:bg-neutral-300 dark:active:bg-neutral-600 touch-manipulation"
        aria-label="Buy data"
        title="Buy data"
      >
        <IconNetwork />
      </button>
    </div>
  </div>
</div>
{/* HEADER */}
<div className="px-3 sm:px-4 pb-3 pt-2 border-b dark:border-neutral-800">

  <div className="mb-2 flex items-center justify-between">

    <div className="flex items-center gap-2 text-base sm:text-lg font-semibold">
      Chats

      {unreadChatCount > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-600 px-2 text-xs font-bold text-white">
          {unreadChatCount}
        </span>
      )}
    </div>

    <div className="flex items-center gap-2">

{/* STATUS */}
<div className="relative">

  {/* Green unread indicator */}
  {hasUnreadStatus && activeTab !== TABS.STATUS && (
    <span
      className="
        absolute
        -top-1
        left-1/2
        -translate-x-1/2
        h-2.5
        w-2.5
        rounded-full
        bg-green-500
        ring-2
        ring-white
        dark:ring-neutral-900
      "
    />
  )}

 <button
  onClick={() => {

    // Clear the green status badge
    statusSocket?.markStatusBadgeRead?.();

    setSelectedItem(null);
    setMenuOpen(false);

    setActiveTab(TABS.STATUS);

  }}
    className="
      rounded-full
      px-3
      py-1.5
      text-sm
      font-medium
      text-neutral-900
      dark:text-white
      transition-colors
      touch-manipulation
      active:bg-neutral-300
      hover:bg-neutral-200
      dark:hover:bg-neutral-700
    "
    title="Status"
    aria-label="Status"
  >
    Status
  </button>

</div>

      {/* MENU */}
      <div
        className="relative"
        ref={menuRef}
      >

               <button
          className="flex items-center justify-center w-10 h-10 rounded-full text-2xl leading-none hover:bg-neutral-200 dark:hover:bg-neutral-700 touch-manipulation active:bg-neutral-300 dark:active:bg-neutral-600"
          onClick={() => setMenuOpen((s) => !s)}
          aria-label="Menu"
        >
          ⋮
        </button>

        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
            />

            <div className="absolute right-0 mt-2 w-52 rounded-lg shadow-lg z-50 bg-white dark:bg-neutral-800 border dark:border-neutral-700 overflow-hidden">

              <button
                onClick={() => {
                  setMenuOpen(false);
                  setCreateGroupMode(true);
                }}
                className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
              >
                <span>➕</span>
                <span>Create group</span>
              </button>

              <div className="h-px bg-neutral-200 dark:bg-neutral-700" />

              <button
                onClick={() => {
                  setMenuOpen(false);

                  const newDefault =
                    storedDefault === TABS.FEED
                      ? TABS.CHATS
                      : TABS.FEED;

                  setDefaultTab?.(newDefault);
                }}
                className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
              >
                <span>🏠</span>
                <span>
                  {storedDefault === TABS.FEED
                    ? "Set Chats as home"
                    : "Set Feed as home"}
                </span>
              </button>

              <div className="h-px bg-neutral-200 dark:bg-neutral-700" />


              {canInstall && (
                <>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleInstall();
                    }}
                    className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
                  >
                    <span>📲</span>
                    <span>Install app</span>
                  </button>

                  <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
                </>
              )}

              {!canInstall && isIOSInstallable && (
                <>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setShowIOSInstallHelp(true);
                    }}
                    className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
                  >
                    <span>📲</span>
                    <span>Install app</span>
                  </button>

                  <div className="h-px bg-neutral-200 dark:bg-neutral-700" />
                </>
              )}
            <button
  onClick={() => {
    setMenuOpen(false);
    window.location.reload();
  }}
  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
>
  <span>🔄</span>
  <span>Refresh</span>
</button>

<div className="h-px bg-neutral-200 dark:bg-neutral-700" />

<button
  onClick={() => {
    setMenuOpen(false);
    setActiveTab(TABS.SETTINGS);
  }}
  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
>
  <span>⚙️</span>
  <span>Settings</span>
</button>

<div className="h-px bg-neutral-200 dark:bg-neutral-700" />
<button
  onClick={() => {
    setMenuOpen(false);
    setActiveTab(TABS.ABOUT);
  }}
  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
>
  <span>ℹ️</span>
  <span>About</span>
</button>

<div className="h-px bg-neutral-200 dark:bg-neutral-700" />

<button
  onClick={() => {
    setMenuOpen(false);
    setActiveTab(TABS.HELP);
  }}
  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2 touch-manipulation"
>
  <span>❓</span>
  <span>Help Center</span>
</button>

<div className="h-px bg-neutral-200 dark:bg-neutral-700" />

{isLoggedIn && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout?.();
                  }}
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2 touch-manipulation"
                >
                  <span>🚪</span>
                  <span>Log out</span>
                </button>
              )}

            </div>
          </>
        )}

      </div>

    </div>

  </div>
    
        {/* SEARCH */}
        <input
          className="w-full rounded-full bg-neutral-100 dark:bg-neutral-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* CHAT LIST */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain px-2 py-2 space-y-0.5 pb-16"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10">
            <div className="text-4xl mb-3">👋</div>
            <div className="font-semibold mb-1">No chats yet</div>
            <div className="text-sm text-neutral-500 mb-4">Find people nearby and start chatting</div>
            <button
              onClick={onOpenConnect}
              className="px-5 py-2.5 rounded-full bg-green-600 text-white text-sm font-medium touch-manipulation active:bg-green-700"
            >
              Connect
            </button>
          </div>
        )}

        {filtered.map((item) => {
          const isActive =
            (item.type === "user"  && selectedItem?.type === "user"  && selectedItem.chatId  === item.chatId)  ||
            (item.type === "group" && selectedItem?.type === "group" && selectedItem.groupId === item.groupId);

          const typingText = getTypingText(item);

          // 🔥 P2P preview takes priority; falls back to last message preview
          const p2pPreview = getP2PPreview(item);

          const preview = p2pPreview
            ? {
                text:        p2pPreview,
                isDeleted:   false,
                isScheduled: false,
                scheduledAt: null,
              }
            : resolveLastMessagePreview(item.lastMessage);

          // Resolved display label — used in every visible text spot below
          const displayLabel =
            item.type === "user"
              ? resolveDisplayName(item)
              : item.name;

          // Unread badge visible only when chat is not open and has unread messages
          const hasUnread = !isActive && item.unreadCount > 0;

          return (
            <div
              key={item.type === "user" ? `user-${item.chatId}` : `group-${item.groupId}`}
              onClick={() => { setActiveTab(TABS.CHATS); setSelectedItem(item); }}
              className={`
                rounded-xl px-3 py-2 cursor-pointer touch-manipulation
                transition-colors
                ${isActive
                  ? "bg-neutral-200 dark:bg-neutral-700"
                  : "bg-white dark:bg-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200 dark:active:bg-neutral-700"}
              `}
            >
              <div className="flex items-center gap-3">
                  {/* AVATAR */}
                  <div
                                      className="relative w-12 h-12 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenAvatar({
                        type:     item.type,
                        image:    item.type === "user" ? item.profileImageUrl : item.groupImageUrl,
                        // pass displayName as the modal title
                        name:     displayLabel,
                        username: item.username || null,
                      });
                    }}
                  >
                    {item.type === "user" && item.profileImageUrl ? (
                      <img
                        src={item.profileImageUrl}
                        alt={displayLabel}
                        className="w-full h-full rounded-full object-cover cursor-pointer"
                      />
                    ) : item.type === "group" && item.groupImageUrl ? (
                      <img
                        src={item.groupImageUrl}
                        alt={item.name}
                        className="w-full h-full rounded-full object-cover cursor-pointer"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-lg">
                        {item.type === "group" ? "👥" : "👤"}
                      </div>
                    )}
                    {item.type === "user" && (
                      <span
                                          className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white dark:border-neutral-900 ${
                          item.online ? "bg-green-500" : "bg-neutral-400"
                        }`}
                      />
                    )}
                  </div>

                {/* RIGHT SIDE: two rows — (name | time) and (preview | badge) */}
                <div className="min-w-0 flex-1">
                  {/* ── Row 1: name + time (time always on the name's line) ── */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-[15px] sm:text-base truncate min-w-0 flex-1">
                      {displayLabel}
                    </div>
                    {item.lastActivityAt && (
                      <div
                        className={`text-xs shrink-0 ${
                          hasUnread
                            ? "text-green-600 font-medium"
                            : "text-neutral-500 dark:text-neutral-400"
                        }`}
                      >
                        {formatTime(item.lastActivityAt)}
                      </div>
                    )}
                  </div>

                  {/* ── Row 2: preview + unread badge ── */}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="text-[13px] sm:text-sm text-neutral-500 dark:text-neutral-400 truncate min-w-0 flex-1">
                      {typingText ? (
                        <span className="italic text-green-600">{typingText}</span>

                      ) : preview?.isScheduled && preview.scheduledAt ? (
                        /* ⏰ Scheduled message — live countdown + message text */
                        <span className="flex items-center gap-1 min-w-0">
                          <ScheduledCountdown scheduledAt={preview.scheduledAt} />
                          {preview.text && (
                            <span className="truncate text-neutral-400 dark:text-neutral-500">
                              · {preview.text}
                            </span>
                          )}
                        </span>

                      ) : preview?.text ? (
                        preview.isDeleted ? (
                          /* 🪦 Tombstone preview */
                          <span className="italic text-neutral-400 dark:text-neutral-500 flex items-center gap-1">
                            <svg
                              viewBox="0 0 17 17"
                              width="11"
                              height="11"
                              fill="currentColor"
                              className="shrink-0 opacity-70"
                            >
                              <path d="M8.5 1a7.5 7.5 0 1 0 0 15A7.5 7.5 0 0 0 8.5 1zm0 1.5a6 6 0 0 1 4.37 10.12L3.38 4.13A6 6 0 0 1 8.5 2.5zm0 12a6 6 0 0 1-4.37-10.12l9.49 9.49A6 6 0 0 1 8.5 14.5z" />
                            </svg>
                            <span className="truncate">{preview.text}</span>
                          </span>
                        ) : (
                          <span>{preview.text}</span>
                        )
                      ) : item.type === "group" ? (
                        `${item.members.length} members`
                      ) : null}
                    </div>

                    {hasUnread && (
                      <div className="flex h-6 min-w-[24px] shrink-0 items-center justify-center rounded-full bg-green-600 px-1.5 text-xs font-semibold text-white">
                        {item.unreadCount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* DESKTOP BOTTOM TABS */}
    
      {!mobileView && (
              <BottomTabs
          activeTab={activeTab}
          onChangeTab={setActiveTab}
          onOpenConnect={onOpenConnect}
          chatCount={activeTab === TABS.CHATS ? 0 : unreadChatCount}
        />
      )}
          {showIOSInstallHelp && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={() => setShowIOSInstallHelp(false)}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Install app
              </span>
              <button
                onClick={() => setShowIOSInstallHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-5 pb-6 text-sm text-neutral-600 dark:text-neutral-300 space-y-2">
              <p>To install this app on your iPhone:</p>
              <p>1. Tap the <strong>Share</strong> icon <span aria-hidden="true">⬆️</span> in Safari's toolbar.</p>
              <p>2. Scroll down and tap <strong>Add to Home Screen</strong>.</p>
              <p>3. Tap <strong>Add</strong> in the top right corner.</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}